import React, { useState, useEffect, useCallback } from 'react';
import {
    BarChart3, Clock, AlertTriangle, Loader2, Info,
} from 'lucide-react';
import { adminAnalytics } from '../../services/api';
import BreakdownTable from './BreakdownTable';
import TrendChart from './TrendChart';

/**
 * UsageTab — Feature usage by event counts + time-spent groundwork.
 *
 * Props:
 *   filters: { days, district_id, campus_id }
 */

const UsageTab = ({ filters = {} }) => {
    const [usage, setUsage] = useState(null);
    const [usageTime, setUsageTime] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const loadData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = {
                days: filters.days || 7,
                ...(filters.district_id && { district_id: filters.district_id }),
                ...(filters.campus_id && { campus_id: filters.campus_id }),
            };
            const [usageRes, timeRes] = await Promise.all([
                adminAnalytics.getFeatureUsage(params),
                adminAnalytics.getUsageTime(params),
            ]);
            setUsage(usageRes.data);
            setUsageTime(timeRes.data);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to load usage data.');
        } finally {
            setLoading(false);
        }
    }, [filters.days, filters.district_id, filters.campus_id]);

    useEffect(() => { loadData(); }, [loadData]);

    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300, flexDirection: 'column', gap: 10 }}>
                <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: '#6366f1' }} />
                <span style={{ fontSize: 13, color: '#6b7280' }}>Loading usage data…</span>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300, flexDirection: 'column', gap: 10 }}>
                <AlertTriangle size={24} style={{ color: '#ef4444' }} />
                <p style={{ fontSize: 14, color: '#ef4444', fontWeight: 500, margin: 0 }}>{error}</p>
            </div>
        );
    }

    const topEvents = usage?.top_events || [];
    const byCategory = usage?.by_category || [];
    const timeStatus = usageTime?.telemetry_status || 'insufficient';

    return (
        <div>
            <div style={{ marginBottom: 24 }}>
                <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: 0 }}>Feature Usage</h2>
                <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0' }}>
                    Event counts and feature activity · last {filters.days || 7} days
                </p>
            </div>

            {/* Layer 1: Event counts */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 28 }}>
                <div>
                    <h3 style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <BarChart3 size={14} style={{ color: '#6366f1' }} />
                        Top Events
                    </h3>
                    <BreakdownTable
                        data={topEvents}
                        labelKey="event_name"
                        valueKey="count"
                        labelHeader="Event"
                        valueHeader="Count"
                        emptyMessage="No events recorded in this period"
                    />
                </div>

                <div>
                    <h3 style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <BarChart3 size={14} style={{ color: '#10b981' }} />
                        By Category
                    </h3>
                    <BreakdownTable
                        data={byCategory}
                        labelKey="category"
                        valueKey="count"
                        labelHeader="Category"
                        valueHeader="Count"
                        emptyMessage="No categories recorded"
                    />
                </div>
            </div>

            {/* Layer 2: Time-spent (groundwork) */}
            <div style={{
                borderTop: '1px solid #e5e7eb', paddingTop: 24,
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                    <Clock size={15} style={{ color: '#8b5cf6' }} />
                    <h3 style={{ fontSize: 14, fontWeight: 600, color: '#111827', margin: 0 }}>
                        Time Spent
                    </h3>
                    {timeStatus !== 'sufficient' && (
                        <span style={{
                            fontSize: 10, fontWeight: 600, padding: '2px 8px',
                            borderRadius: 4,
                            background: timeStatus === 'partial' ? '#fef3c7' : '#f1f5f9',
                            color: timeStatus === 'partial' ? '#92400e' : '#64748b',
                        }}>
                            {timeStatus === 'partial' ? 'Partial Data' : 'Collecting'}
                        </span>
                    )}
                </div>

                {/* Telemetry status banner */}
                {timeStatus !== 'sufficient' && (
                    <div style={{
                        display: 'flex', alignItems: 'flex-start', gap: 10,
                        padding: '12px 16px', borderRadius: 10,
                        background: '#f8fafc', border: '1px solid #e2e8f0',
                        marginBottom: 20,
                    }}>
                        <Info size={16} style={{ color: '#6366f1', flexShrink: 0, marginTop: 1 }} />
                        <div>
                            <p style={{ fontSize: 13, color: '#374151', fontWeight: 500, margin: 0 }}>
                                {usageTime?.note || 'Telemetry not yet sufficient for full time-spent analytics.'}
                            </p>
                            <p style={{ fontSize: 12, color: '#6b7280', margin: '4px 0 0' }}>
                                Fuller trends will appear as session and route lifecycle events accumulate.
                            </p>
                        </div>
                    </div>
                )}

                {/* Session stats */}
                {usageTime && (usageTime.session_count > 0 || usageTime.by_route?.length > 0) ? (
                    <>
                        <div style={{
                            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                            gap: 12, marginBottom: 20,
                        }}>
                            <MiniStat label="Avg Session" value={formatDuration(usageTime.avg_session_duration_sec)} />
                            <MiniStat label="Median Session" value={formatDuration(usageTime.median_session_duration_sec)} />
                            <MiniStat label="Total Active" value={formatDuration(usageTime.total_active_seconds)} />
                            <MiniStat label="Sessions" value={usageTime.session_count || 0} />
                        </div>

                        {usageTime.by_route?.length > 0 && (
                            <div style={{ marginBottom: 16 }}>
                                <h4 style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                                    Time by Route
                                </h4>
                                <BreakdownTable
                                    data={usageTime.by_route.map(r => ({
                                        ...r, seconds_display: formatDuration(r.seconds),
                                    }))}
                                    labelKey="route"
                                    valueKey="seconds_display"
                                    labelHeader="Route"
                                    valueHeader="Time"
                                    emptyMessage="No route data yet"
                                />
                            </div>
                        )}

                        {usageTime.by_feature_area?.length > 0 && (
                            <div>
                                <h4 style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                                    Time by Feature Area
                                </h4>
                                <BreakdownTable
                                    data={usageTime.by_feature_area.map(r => ({
                                        ...r, seconds_display: formatDuration(r.seconds),
                                    }))}
                                    labelKey="feature_area"
                                    valueKey="seconds_display"
                                    labelHeader="Feature Area"
                                    valueHeader="Time"
                                    emptyMessage="No feature area data yet"
                                />
                            </div>
                        )}
                    </>
                ) : (
                    <div style={{
                        textAlign: 'center', padding: '32px 0', color: '#94a3b8',
                    }}>
                        <Clock size={32} style={{ margin: '0 auto 8px', opacity: 0.4 }} />
                        <p style={{ fontSize: 13, margin: 0 }}>
                            No session data yet. Time-spent data will appear as telemetry accumulates.
                        </p>
                    </div>
                )}
            </div>

            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};


const MiniStat = ({ label, value }) => (
    <div style={{
        background: '#f8fafc', borderRadius: 10, padding: '14px 16px',
        border: '1px solid #f1f5f9',
    }}>
        <div style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 4 }}>
            {label}
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#111827', fontVariantNumeric: 'tabular-nums' }}>
            {value}
        </div>
    </div>
);


const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '0s';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    const h = Math.floor(seconds / 3600);
    const m = Math.round((seconds % 3600) / 60);
    return `${h}h ${m}m`;
};


export default UsageTab;
